import { CheckboxState } from '../../../common/components/checkboxes/CheckboxState';
import { ReactCheckboxTristate } from '../../../common/react/components/ReactCheckboxTristate';
import { SiteRolePickerMenu } from '../../../common/react/components/SiteRolePickerMenu';
import { ISiteNameWithId } from '../../../ts/ServerApi/types/ISiteNameWithId';
import { SiteRoles } from '../../../ts/ServerApi/types/ISiteRole';
import { DefaultSiteRole, SiteRoleOption, SiteRoleOptionsResource } from '../../services/resources/SiteRoleOptions';
import { SiteRolesResource } from '../../services/resources/SiteRoles';
import { UserRoleSelections } from '../EditSiteMembershipDialog';
import { SiteRoleCheckboxPropsDictionary, SiteRoleSelectorPropsDictionary, SiteRolesPickerContainerState } from '../SiteRolesPickerContainer';

const ADMIN_SITE_ROLE_OPTION = SiteRoles.ServerAdministrator;

export class SiteRolesPickerDataGridHelper {

    /**
     *
     * @param state
     * @param busy
     * Update disabled state based upon if busy
     */
    public static getStateOnBusyChange(state: SiteRolesPickerContainerState, busy: boolean): SiteRolesPickerContainerState {
        let siteRowCheckboxPropMap: SiteRoleCheckboxPropsDictionary = {};
        let siteRowDropdownPropMap: SiteRoleSelectorPropsDictionary = {};
        for (let prop in state.siteRowCheckboxPropMap) {
            let disabled = busy;
            siteRowCheckboxPropMap[prop] = {...state.siteRowCheckboxPropMap[prop], disabled};
        }
        for (let prop in state.siteRowDropdownPropMap) {
            let isDisabled = busy;
            siteRowDropdownPropMap[prop] = {...state.siteRowDropdownPropMap[prop], isDisabled};
        }
        return {...state, siteRowCheckboxPropMap, siteRowDropdownPropMap};
    }

    /**
     * 
     * @param state 
     * @param busy 
     * Update disabled state based upon if busy
     */
    public static getStateOnModifyRoles(state: SiteRolesPickerContainerState, userRoleSelections: UserRoleSelections, siteRolesResource: SiteRoleOptionsResource): SiteRolesPickerContainerState {
        let siteRowCheckboxPropMap: SiteRoleCheckboxPropsDictionary = {};
        let siteRowDropdownPropMap: SiteRoleSelectorPropsDictionary = {};
        let adminCheckboxState: CheckboxState;

        adminCheckboxState = SiteRolesPickerDataGridHelper.getAdminCheckboxStateOnModify(userRoleSelections, adminCheckboxState);

        for (let prop in state.siteRowCheckboxPropMap) {

            if (adminCheckboxState === CheckboxState.checked) {
                siteRowCheckboxPropMap[prop] = {...state.siteRowCheckboxPropMap[prop], checkboxState: CheckboxState.checked, disabled: true};
            } else if (adminCheckboxState === CheckboxState.mixed) {
                siteRowCheckboxPropMap[prop] = {...state.siteRowCheckboxPropMap[prop], checkboxState: CheckboxState.mixed, disabled: true};
            } else {
                siteRowCheckboxPropMap[prop] = SiteRolesPickerDataGridHelper.getRowCheckboxPropsOnModify(userRoleSelections, prop, siteRowCheckboxPropMap, state);
            }
        }

        for (let prop in state.siteRowDropdownPropMap) {

            if (adminCheckboxState === CheckboxState.checked) {
                let selectedRole = siteRolesResource.getSiteRoleOptionBySiteRole(SiteRoles.ServerAdministrator);
                let siteRoleOptions = siteRolesResource.getAllSiteRoleOptions();
                siteRowDropdownPropMap[prop] = {...state.siteRowDropdownPropMap[prop], selectedRole: selectedRole, isDisabled: true, siteRoleOptions: siteRoleOptions};
            } else if (adminCheckboxState === CheckboxState.mixed) {
                let selectedRole = siteRolesResource.getSiteRoleOptionBySiteRole(SiteRolesResource.Mixed);
                siteRowDropdownPropMap[prop] = {...state.siteRowDropdownPropMap[prop], selectedRole: selectedRole, isDisabled: true};
            } else {
                siteRowDropdownPropMap[prop] = SiteRolesPickerDataGridHelper.getRowDropdownPropsOnModify(userRoleSelections, prop, siteRowDropdownPropMap, state, siteRolesResource);
            }
        }
        return {...state, siteRowCheckboxPropMap, siteRowDropdownPropMap, adminCheckboxState};
    }

    private static getAdminCheckboxStateOnModify(userRoleSelections: UserRoleSelections, adminCheckboxState: CheckboxState) {
        if (userRoleSelections.numSelectedServerAdmins === 0) {
            adminCheckboxState = CheckboxState.empty;
        } else if (userRoleSelections.numSelectedServerAdmins < userRoleSelections.numSelectedUsers) {
            adminCheckboxState = CheckboxState.mixed;
        } else {
            adminCheckboxState = CheckboxState.checked;
        }
        return adminCheckboxState;
    }

    public static getRowDropdownPropsOnModify(userRoleSelections: UserRoleSelections, siteId: string, siteRowDropdownPropMap: SiteRoleSelectorPropsDictionary, state: SiteRolesPickerContainerState, siteRolesResource: SiteRoleOptionsResource): SiteRolePickerMenu.Props {
        const siteInfo = userRoleSelections.userCountsAndRolesBySiteId[siteId];
        const numSelectedSiteMembers = siteInfo ? siteInfo.count : 0;
        const siteRoleOptions = siteRolesResource.getAllSiteRoleOptionsExcept(SiteRoles.ServerAdministrator);
        if (numSelectedSiteMembers === userRoleSelections.numSelectedUsers) {
            // if all of the selected users are in all the sites, use the aggregated role
            let selectedRole = siteRolesResource.getSiteRoleOptionBySiteRole(siteInfo.siteRole);
            return { ...state.siteRowDropdownPropMap[siteId], selectedRole: selectedRole, isDisabled: false, siteRoleOptions: siteRoleOptions};
        }
        else if (numSelectedSiteMembers > 0) {
            // some of the selected users are site members.
            let selectedRole = siteRolesResource.getSiteRoleOptionBySiteRole(SiteRolesResource.Mixed);
            return { ...state.siteRowDropdownPropMap[siteId], selectedRole: selectedRole, isDisabled: false, siteRoleOptions: siteRoleOptions};
        }
        else {
            // numSelectedSiteMembers === 0
            let selectedRole = siteRolesResource.getSiteRoleOptionBySiteRole(SiteRoles.Unlicensed);
            return { ...state.siteRowDropdownPropMap[siteId], selectedRole: selectedRole, isDisabled: false, siteRoleOptions: siteRoleOptions};
        }
    }

    public static getRowCheckboxPropsOnModify(userRoleSelections: UserRoleSelections, siteId: string, siteRowCheckboxPropMap: SiteRoleCheckboxPropsDictionary, state: SiteRolesPickerContainerState): ReactCheckboxTristate.Props {
        const siteInfo = userRoleSelections.userCountsAndRolesBySiteId[siteId];
        const numSelectedSiteMembers = siteInfo ? siteInfo.count : 0;
        if (numSelectedSiteMembers === userRoleSelections.numSelectedUsers) {
            return { ...state.siteRowCheckboxPropMap[siteId], checkboxState: CheckboxState.checked, disabled: false};
        }
        else if (numSelectedSiteMembers > 0) {
            return { ...state.siteRowCheckboxPropMap[siteId], checkboxState: CheckboxState.mixed, disabled: false};
        }
        else {
            return { ...state.siteRowCheckboxPropMap[siteId], checkboxState: CheckboxState.empty, disabled: false};
        }
    }

     /**
     *
     * @param state
     * @param site
     * @param siteRoleOption
     * @param siteRolesResource
     * Update corresponding dropdown with selected role
     */
    public static getStateOnSiteRoleSelected(state: SiteRolesPickerContainerState, site: ISiteNameWithId, siteRoleOption: SiteRoleOption, siteRolesResource: SiteRoleOptionsResource): SiteRolesPickerContainerState {
        let siteRowDropdownPropMap: SiteRoleSelectorPropsDictionary = {...state.siteRowDropdownPropMap};
        let selectedRole = siteRolesResource.getSiteRoleOptionBySiteRole(siteRoleOption.role);
        siteRowDropdownPropMap[site.id].selectedRole = selectedRole;
        return {...state, siteRowDropdownPropMap};
    }

     /**
     *
     * @param state
     * @param row
     * @param siteRolesResource
     * Update checkbox and dropdown on click
     */
    public static getStateOnCellClicked(state: SiteRolesPickerContainerState, row: any, siteRolesResource: SiteRoleOptionsResource): SiteRolesPickerContainerState {
        let siteRowCheckboxPropMap: SiteRoleCheckboxPropsDictionary = this.updateCheckboxMap(row, state);
        let siteRowDropdownPropMap: SiteRoleSelectorPropsDictionary = this.updateDropdownMap(row, state, siteRolesResource);
        return {...state, siteRowCheckboxPropMap, siteRowDropdownPropMap};
    }

    private static updateCheckboxMap(site: ISiteNameWithId, state: SiteRolesPickerContainerState): SiteRoleCheckboxPropsDictionary{
        let siteToCheckboxPropMap: SiteRoleCheckboxPropsDictionary = {...state.siteRowCheckboxPropMap};
        let checkboxProps = siteToCheckboxPropMap[site.id];
        let checkboxState = CheckboxState.toggle(checkboxProps.checkboxState as string);
        siteToCheckboxPropMap[site.id] = {...checkboxProps, checkboxState};
        return siteToCheckboxPropMap;
    }

    private static updateDropdownMap(site: ISiteNameWithId, state: SiteRolesPickerContainerState, siteRolesResource: SiteRoleOptionsResource): SiteRoleSelectorPropsDictionary{
        let siteToDropdownPropMap: SiteRoleSelectorPropsDictionary = {...state.siteRowDropdownPropMap};
        let selectedRole = siteRolesResource.getSiteRoleOptionBySiteRole(DefaultSiteRole);
        siteToDropdownPropMap[site.id] = {...siteToDropdownPropMap[site.id], selectedRole};
        return siteToDropdownPropMap;
    }
  }
